import React from 'react';
import PropTypes from 'prop-types';

import { ModalBody } from '@eb/eds-modal';

import { translationPropType } from '@eb/i18n';
import gettext from '@eb/gettext';
import ProtectedEventIllustration from '../../resources/protected-event-illustration';
import InviteOnlyCTA from './InviteOnlyCTA';
import PasswordOnlyCTA from './PasswordOnlyCTA';

import {
    INVITATION_REQUIRED,
    PASSWORD_EXPIRED,
    PASSWORD_REQUIRED,
} from '../../constants';

import './ProtectedEventPage.scss';

const PROTECTED_EVENT_TERMINOLOGY = {
    [INVITATION_REQUIRED]: {
        headerText: gettext('This event is invite only'),
        // translators: please pay extra care to the <a> tag and do not alter any of its properties
        ctaText: (eventUrl) =>
            gettext(
                'Check your email for an invitation. If you don\'t have one, <a href="%(eventUrl)s" id="invitation-protected-cta-eb-link" target="_blank">contact the organizer</a>',
                { eventUrl },
            ),
    },
    [PASSWORD_REQUIRED]: {
        headerText: gettext('This event is password protected'),
        ctaText: gettext('Enter password'),
    },
    [PASSWORD_EXPIRED]: {
        headerText: gettext(
            "This event's password has expired or was changed. Try password again",
        ),
        ctaText: gettext('Enter password'),
    },
};

const PrivacySettingCTA = ({
    privacySetting,
    eventUrl,
    onCTAClick,
    eventId,
}) => {
    switch (privacySetting) {
        case INVITATION_REQUIRED:
            return (
                <InviteOnlyCTA
                    ctaText={PROTECTED_EVENT_TERMINOLOGY[
                        INVITATION_REQUIRED
                    ].ctaText(eventUrl)}
                    eventUrl={eventUrl}
                    onCTAClick={onCTAClick}
                    eventId={eventId}
                />
            );
        case PASSWORD_REQUIRED:
        case PASSWORD_EXPIRED:
            return (
                <PasswordOnlyCTA
                    ctaText={
                        PROTECTED_EVENT_TERMINOLOGY[privacySetting].ctaText
                    }
                    eventUrl={eventUrl}
                    onCTAClick={onCTAClick}
                    eventId={eventId}
                />
            );
        default:
            return false;
    }
};

class ProtectedEventPage extends React.PureComponent {
    static propTypes = {
        /**
         * Props related to the checkout template
         */
        eventTitle: PropTypes.string.isRequired,
        /**
         * Callback invoked when the user clicks the 'Back' link on the form or uses the browser/native back button.
         */
        eventDate: translationPropType.isRequired,
        /**
         * Callback invoked when the registration time limit expires.
         */
        eventId: PropTypes.string.isRequired,
        /**
         * Used for Google Analytics tracking for cart summary icon in footer
         */
        eventUrl: PropTypes.string.isRequired,
        /**
         * The tickets selected for the current order.
         */
        privacySetting: PropTypes.string.isRequired,
        /**
         * True if the registration time limit has expired.
         */
        onCTAClick: PropTypes.func.isRequired,
    };

    render() {
        const {
            eventTitle,
            eventDate,
            eventId,
            eventUrl,
            privacySetting,
            onCTAClick,
        } = this.props;

        return (
            <ModalBody title={eventTitle} secondaryTitle={eventDate}>
                <div className="eds-g-grid">
                    <div className="eds-g-group eds-text--center">
                        <div className="protected-event-illustration__container">
                            <div className="eds-bg-color--grey-200 protected-event-illustration__background" />
                            <ProtectedEventIllustration
                                className="eds-l-mar-top-3 protected-event-illustration__svg"
                                data-spec="protected-event-illustration"
                            />
                        </div>

                        <div className="eds-g-cell eds-g-cell-12-12 eds-l-pad-all-8">
                            <section>
                                <h2 className="eds-text-hs eds-align--center-horizontal">
                                    <span
                                        className="eds-g-cell-sw-6-12 eds-g-cell-md-5-12"
                                        data-spec="protected-event-title"
                                    >
                                        {
                                            PROTECTED_EVENT_TERMINOLOGY[
                                                privacySetting
                                            ].headerText
                                        }
                                    </span>
                                </h2>

                                <div
                                    className="eds-align--center-horizontal"
                                    data-spec="protected-event-content"
                                >
                                    <PrivacySettingCTA
                                        privacySetting={privacySetting}
                                        eventUrl={eventUrl}
                                        onCTAClick={onCTAClick}
                                        eventId={eventId}
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </ModalBody>
        );
    }
}

export default ProtectedEventPage;
